import { AlmeSessionData } from "@alme/session-ts/AlmeSessionData";
import { AlmeSessionDataLoaderBase } from "@alme/session-ts/AlmeSessionDataLoaderBase";
import { AlmeSessionDataUtils } from "@alme/session-ts/AlmeSessionDataUtils";
import { StorageLoaderBase } from "@alme/storage-ts/StorageLoaderBase";
import { AlmeResponseNext } from "@alme/types/response/AlmeResponse";
import { useCallback, useEffect } from "react";
import { AlmeSavedAppState } from "../components/AlmeApp/AlmeAppState";

import defaultLogger from '../Logger';

export function loadAlmeSessionData(sessionDataLoader: AlmeSessionDataLoaderBase): AlmeSessionData {
    let sessionData: AlmeSessionData | null = null;
    try {
        sessionData = sessionDataLoader.GetAlmeSessionData();
    } catch (ex) {
        defaultLogger.error(ex);
    }
    if (!sessionData) {
        sessionData = AlmeSessionDataUtils.CreateAlmeSessionData();
    }
    return sessionData;
}

export function useSaveAlmeSessionData(almeSessionData: AlmeSessionData, sessionDataLoader: AlmeSessionDataLoaderBase): void {
    useEffect(() => {
        let sessionData: AlmeSessionData | null = null;
        try {
            sessionData = sessionDataLoader.GetAlmeSessionData();
        } catch (ex) {
            defaultLogger.error(ex);
        }
        if (sessionData && (sessionData?.userId !== almeSessionData.userId ||
            sessionData?.sessionId !== almeSessionData?.sessionId ||
            sessionData.lastActivityTime !== almeSessionData?.lastActivityTime)
        ) {
            try {
                sessionDataLoader.UpdateAlmeSessionData(almeSessionData);
            } catch (ex) {
                defaultLogger.error(ex);
            }
        }
    }, [almeSessionData, sessionDataLoader]);
}

export function useAlmeSessionDataCallback(
    almeSessionData: AlmeSessionData,
    setAlmeSessionData: (almeSessionData: AlmeSessionData) => void,
): (response: AlmeResponseNext | AlmeResponseNext[]) => void {
    return useCallback((response: AlmeResponseNext | AlmeResponseNext[]): void => {
        let almeResponse: AlmeResponseNext;
        if (response && (response as AlmeResponseNext[]).length) {
            // get the last history item to set alme session data.
            almeResponse = (response as AlmeResponseNext[])[(response as AlmeResponseNext[]).length - 1];
        } else {
            almeResponse = response as AlmeResponseNext;
        }

        if (almeResponse && almeResponse.userId && almeSessionData.userId !== almeResponse.userId) {
            const newSessionData: AlmeSessionData = {
                sessionId: almeSessionData.sessionId,
                userId: almeResponse.userId,
                lastActivityTime: almeSessionData.lastActivityTime,
            };
            setAlmeSessionData(newSessionData);
        }
    }, [almeSessionData.sessionId, almeSessionData.userId, almeSessionData.lastActivityTime, setAlmeSessionData]);
}

export function useSaveLastActivityTime(
    almeSessionData: AlmeSessionData,
    setAlmeSessionData: (almeSessionData: AlmeSessionData) => void,
): (lastActivityTime: string) => void {

    return useCallback((lastActivityTime) => {

        if (lastActivityTime !== almeSessionData.lastActivityTime) {
            setAlmeSessionData({ ...almeSessionData, lastActivityTime });
        }
    }, [almeSessionData, setAlmeSessionData]);
}

export function useClearSessionData(
    sessionDataLoader: AlmeSessionDataLoaderBase,
    savedAppState: StorageLoaderBase<AlmeSavedAppState>,
    setAlmeSessionData: (almeSessionData: AlmeSessionData) => void,
    clearAppData: () => void,
): () => void {
    return useCallback((): void => {
        try {
            clearAppData();
        } catch (ex) {
            defaultLogger.error(ex);
        }
        try {
            sessionDataLoader.ClearAlmeSessionData();
        } catch (ex) {
            defaultLogger.error(ex);
        }
        try {
            savedAppState.ClearStorage();
        } catch (ex) {
            defaultLogger.error(ex);
        }
        setAlmeSessionData(AlmeSessionDataUtils.CreateAlmeSessionData());
    }, [clearAppData, savedAppState, sessionDataLoader, setAlmeSessionData]);
}
